import { RequestFlagKeyData } from "flagsup-ts";
import { IUserDetailsInfo } from "teko-oauth2";

const FLAG_KEYS = {
  // epic flag keys
  UNIFY_ORDER_STATUS_LABEL: "epic_omni_1058",
  REDESIGN_FLOW_MANUAL_SELLERS: "epic_omni_1244",
  ALLOW_SELLER_SETUP_ORDER_NOTIFICATION: "epic_ecom_295",
  AUTO_CREATE_SR_FOR_ITEMS: "epic_omni_1301",
  IMPROVEMENT_PO_INVOICE_DECLARATION: "epic_omni_1918",
  IMPROVE_STATE_BUDGET_UNIT_CODE_VALIDATION: "epic_omni_2093",
  // OMNI-TIC2026 (TIC-26) — the ONE key gating the support-assistant workspace: 4 routes in
  // `routes/support.ts` + the floating launcher in `SupportLauncherMount`.
  //
  // It now has its OWN key. Before, it borrowed `omni_internal_support_order_wave1` — the key of the
  // DELETED order-resync wave — which meant flipping that flag moved two unrelated features at once,
  // and neither could be rolled out without the other. That is what MR !2487 was pulled up on.
  //
  // ❗Two things about this flag that are easy to get wrong:
  //
  //  1. **It is FE-only.** The BFF has no flag gate for this epic at all — T34 was closed by REMOVING
  //     it (`8a0f060b8c`, -363 lines), so `SupportAssistantConfig` has no flag-key field. There is no
  //     "both sides must match" here. What protects the server side is permission + tenant scope,
  //     checked on every read.
  //  2. **A key MISSING from Flagsup does not hide the routes.** `utils/flagsup.ts` returns
  //     `true` when the key is absent (`!flagsData[flagKey] => visible`), so an unregistered key
  //     leaves the routes OPEN and only the IAM permission gates them. The launcher is the opposite —
  //     it fails CLOSED (`?.enabled === true`). So this string has to EXIST in Flagsup for the flag to
  //     mean anything on the routes.
  //
  // [Needs Info: the string below follows the repo convention (`epic_<project>_<number>`, cf.
  // `epic_omni_1058` / `epic_ecom_295`) but still has to be CREATED in Flagsup, plus a pilot cohort.
  // Owners per T34: architecture (staff-bff owner) + PO. Until it is created, see point 2 — the routes
  // are gated by permission only.]
  SUPPORT_ASSISTANT_WORKSPACE: "epic_tic_26",

  // non-epic flag keys
  REFRESH_WITH_GUARANTEE_OMPRODUCT_1052: "REFRESH_WITH_GUARANTEE_OMPRODUCT_1052",
  REFRESH_WITH_GUARANTEE_OMPRODUCT_1171: "REFRESH_WITH_GUARANTEE_OMPRODUCT_1171",
  SHOW_SURVEY_POPUP_OMPRODUCT_1714: "SHOW_SURVEY_POPUP_OMPRODUCT_1714",
  CALL_BFF_CATALOG_OMPRODUCT_1100: "CALL_BFF_CATALOG_OMPRODUCT_1100",
  BUTTON_EXPORT_DATA_IN_ALL_TAB_OMPRODUCT_1924: "BUTTON_EXPORT_DATA_IN_ALL_TAB_OMPRODUCT_1924",
  IMPROVE_BILLING_INFO_EDIT_HWF_1482: "IMPROVE_BILLING_INFO_EDIT_HWF_1482",
  CHECK_ALLOW_PARTIAL_RETURN_WITH_ACTION_CODE: "hwf_1961",
  USE_SITE_SELECTOR_WITH_PAGINATION: "use_site_selector_with_pagination",
  USE_NEW_TAX_CODE_FORMAT: "use_new_tax_code_format",
  USER_TERMINAL_REPORT_RECONCILIATION: "user_terminal_report_reconciliation",
  OMPRODUCT_4582: "OMPRODUCT_4582",
  WMS20_8220: "wms20_8220",
  SHOW_REPRESENTATIVE_BUYER: "feature_pi_9176",
  EPIC_OMNI_1825: "epic_omni_1825",
  WMS20_8514: "wms20_8514",
  EPIC_OMNI_1929: "epic_omni_1929",
};

// Extend flag keys
// Flag defined within this object will be treated as a flag that requires entityId to be evaluated
// The flag will be evaluate with logic:
// 1. If the flag is enabled => return true
// 2. If the flag is disabled => check if the entityId is in the whitelist => return true
// 3. If the flag is disabled and entityId is not in the whitelist => return false
type RequestFlagKeysWithCustomEntityId = {
  [flagKey in ExtendFlagKeys]: RequestFlagKeyData<IUserDetailsInfo>;
};

// Define the flag keys in order to support suggestion and auto-complete in IDE
type ExtendFlagKeys =
  | "MANAGE_OFFLINE_STORE_FOR_SELLER"
  | "SITE_LIST_WITH_ACTIVE_STATUS"
  | "EPIC_OMNI_1277"
  | "EPIC_OMNI_1399"
  | "EPIC_ECOM_489"
  | "EPIC_OMNI_1504"
  | "EPIC_OMNI_1407"
  | "EPIC_OMNI_1462"
  | "SEARCH_SELLER_ORDER_WITH_EXPORT_SITE"
  | "FORCE_CREATE_SHIPMENT_ON_SELLER_ORDER"
  | "GET_USER_TERMINALS_SELLER"
  | "GET_USER_TERMINALS_PLATFORM"
  | "SHOW_INVENTORY_AFTER_CLICK_SITE"
  | "ALLOW_TO_USE_API_SELLER_LIST_HWF_2012"
  | "EPIC_OMNI_841_PHASE_2"
  | "IMPROVEMENT_PURCHASE_INVOICE"
  | "REFUND_REQUEST_UPGRADE_FILTER"
  | "SHOW_ORDER_MEMBER_INFO"
  | "EPIC_OMNI_1589"
  | "OMPRODUCT_4543"
  | "INVENTORY_TRACKING_V2"
  | "EPIC_OMNI_1595"
  | "EPIC_OMNI_1628"
  | "EPIC_OMNI_1616"
  | "SHOW_WARNING_CANCEL_SHIPMENT"
  | "LIMIT_FIELDS_UPDATED_BILLING_INFO_AFTER_ORDER_EXPORT"
  | "ALLOW_SEARCH_CROSS_PLATFORM"
  | "EPIC_OMNI_1675"
  | "EPIC_OMNI_2095"
  | "WMS20_8375"
  | "EPIC_OMNI_1873"
  | "SHOW_VAT_INVOICE_GUIDE_LINK"
  | "SHOW_BRANDING_ON_SELLER_BOL_LABEL"
  | "SHOW_TRANSPORT_NUMBER_OF_PACKAGES";

const EXTENDED_FLAG_KEYS: RequestFlagKeysWithCustomEntityId = {
  MANAGE_OFFLINE_STORE_FOR_SELLER: {
    key: "epic_omni_595_seller",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  SITE_LIST_WITH_ACTIVE_STATUS: {
    key: "epic_omni_1139",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  EPIC_OMNI_1277: {
    key: "epic_omni_1277",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  EPIC_OMNI_1399: {
    key: "epic_omni_1399",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  EPIC_ECOM_489: {
    key: "epic_ecom_489",
    entityId: "om_product",
  },
  EPIC_OMNI_1504: {
    key: "epic_omni_1504",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  EPIC_OMNI_1407: {
    key: "epic_omni_1407",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  EPIC_OMNI_1462: {
    key: "epic_omni_1462",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  SEARCH_SELLER_ORDER_WITH_EXPORT_SITE: {
    key: "search_seller_order_with_export_site",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  FORCE_CREATE_SHIPMENT_ON_SELLER_ORDER: {
    key: "hwf_1924",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  GET_USER_TERMINALS_SELLER: {
    key: "epic_ecom_432_seller",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  GET_USER_TERMINALS_PLATFORM: {
    key: "epic_ecom_432_platform",
    getEntityId: (userDetail) => String(userDetail?.meta_data?.platformId),
  },
  SHOW_INVENTORY_AFTER_CLICK_SITE: {
    key: "show_inventory_after_click_site",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  ALLOW_TO_USE_API_SELLER_LIST_HWF_2012: {
    key: "allow_to_use_api_seller_list_hwf_2012",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  EPIC_OMNI_841_PHASE_2: {
    key: "epic_omni_841_phase_2",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  IMPROVEMENT_PURCHASE_INVOICE: {
    key: "improvement_purchase_invoice",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  REFUND_REQUEST_UPGRADE_FILTER: {
    key: "refund_request_upgrade_filter",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  SHOW_ORDER_MEMBER_INFO: {
    key: "show_order_member_info",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  EPIC_OMNI_1589: {
    key: "epic_omni_1589_warehouse_central",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  OMPRODUCT_4543: {
    key: "OMPRODUCT_4543",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  INVENTORY_TRACKING_V2: {
    key: "inventory_tracking_v2",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  EPIC_OMNI_1595: {
    key: "epic_omni_1595",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  EPIC_OMNI_1628: {
    key: "epic_omni_1628",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  EPIC_OMNI_1616: {
    key: "epic_omni_1616",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  SHOW_WARNING_CANCEL_SHIPMENT: {
    key: "feature_pi_8660",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  LIMIT_FIELDS_UPDATED_BILLING_INFO_AFTER_ORDER_EXPORT: {
    key: "feature_pi_8668",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  ALLOW_SEARCH_CROSS_PLATFORM: {
    key: "platform_id_allow_to_search_cross_platform",
    getEntityId: (userDetail) => String(userDetail?.meta_data?.platformId),
  },
  EPIC_OMNI_1675: {
    key: "epic_omni_1675",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  EPIC_OMNI_2095: {
    key: "epic_omni_2095",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  WMS20_8375: {
    key: "wms20_8375",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  EPIC_OMNI_1873: {
    key: "epic_omni_1873",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  SHOW_VAT_INVOICE_GUIDE_LINK: {
    key: "feature_omni_752",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id,
  },
  // Print the order's platform branding (logo, contact, barcode) on the shipping label of the seller order detail,
  // the platform order detail always has it. Evaluated by the user's first seller
  SHOW_BRANDING_ON_SELLER_BOL_LABEL: {
    key: "seller_id_show_branding_on_seller_bol_label",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id?.split(",")[0]?.trim(),
  },
  // Show the number of packages of the transport (LOP) instead of the delivery request's on the order detail.
  // Evaluated by the user's first seller since LOP looks the transport up with that seller
  SHOW_TRANSPORT_NUMBER_OF_PACKAGES: {
    key: "seller_id_show_transport_number_of_packages",
    getEntityId: (userDetail) => userDetail?.meta_data?.seller_id?.split(",")[0]?.trim(),
  },
};

export default {
  FLAG_KEYS,
  EXTENDED_FLAG_KEYS,
};
